import React from 'react';
import { motion } from 'framer-motion';
import { containerVariants } from '../../../animations/variants';

interface Venture {
  name: string;
  mark: string;
  category: string;
  description: string;
  active: boolean;
  colors: {
    badge: string;
    bar: string;
    border: string;
  };
}

const ventures: Venture[] = [
  {
    name: 'Eight Em',
    mark: '8',
    category: 'Consumer / Hair Science',
    description: 'A consumer hair science brand built on real formulation research.',
    active: true,
    colors: {
      badge: 'bg-venture-em/10 text-venture-em',
      bar: 'bg-venture-em',
      border: 'border-ink-line',
    },
  },
  {
    name: 'True Measure',
    mark: 'T',
    category: 'Construction',
    description: 'A construction business built on disciplined process and execution.',
    active: true,
    colors: {
      badge: 'bg-venture-measure/10 text-venture-measure',
      bar: 'bg-venture-measure',
      border: 'border-ink-line',
    },
  },
  {
    name: 'PropProofs',
    mark: 'P',
    category: 'PropTech / Lab',
    description: 'A property technology venture in active development.',
    active: true,
    colors: {
      badge: 'bg-venture-proof/10 text-venture-proof',
      bar: 'bg-venture-proof',
      border: 'border-ink-line',
    },
  },
  {
    name: 'Future Ventures',
    mark: '+',
    category: 'Whatever Should Exist Next',
    description: "The next venture, wherever the constraint and the opportunity lead.",
    active: false,
    colors: {
      badge: 'bg-transparent text-ink-muted border border-dashed border-ink-muted/50',
      bar: 'bg-transparent',
      border: 'border-ink-line',
    },
  },
];

const cardVariants = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } },
};

interface Props {
  sectionVariants: any;
  itemVariants: any;
}

export const VenturesSection: React.FC<Props> = ({ sectionVariants, itemVariants }) => {
  return (
    <motion.section
      id="ventures"
      className="py-28 md:py-36 px-6 md:px-12 bg-neutral-lightest border-t border-ink-line"
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.1 }}
      variants={sectionVariants}
    >
      <div className="max-w-7xl mx-auto">
        <div className="max-w-3xl mb-24">
          <motion.p
            className="text-xs uppercase tracking-eyebrow text-ink-muted mb-8"
            variants={itemVariants}
          >
            The Portfolio
          </motion.p>
          <motion.h2
            className="font-display text-4xl md:text-6xl leading-tight tracking-tight mb-10"
            variants={itemVariants}
          >
            One company. <span className="italic">Different industries.</span>
          </motion.h2>
          <motion.div className="space-y-6" variants={itemVariants}>
            <p className="text-xl md:text-2xl text-ink leading-relaxed">
              Founded by David and Tamara McKinney, TND Labs exists to pursue ideas worth building.
            </p>
            <p className="text-lg text-ink-soft leading-relaxed">
              We don't begin with an industry or a technology. We begin with a problem, develop a
              point of view, and determine what should exist to solve it. Sometimes that's software.
              Sometimes it's a physical product. Sometimes it's an operating business.
            </p>
            <p className="text-lg text-ink leading-relaxed">
              Different industries. Same approach: question assumptions, build deliberately, test in
              the real world, and keep improving.
            </p>
          </motion.div>
        </div>

        <motion.div
          className="grid sm:grid-cols-2 lg:grid-cols-4 border-t border-l border-ink-line"
          variants={containerVariants}
        >
          {ventures.map((venture) => (
            <motion.div
              key={venture.name}
              variants={cardVariants}
              className={`group relative bg-white border-r border-b border-ink-line p-8 transition-colors duration-300 ${
                venture.active ? 'hover:bg-neutral-lightest' : ''
              }`}
            >
              <div className={`absolute top-0 left-0 h-[3px] w-full ${venture.colors.bar}`} />
              <div
                className={`w-12 h-12 rounded-full flex items-center justify-center font-display text-lg mb-8 ${venture.colors.badge}`}
              >
                {venture.mark}
              </div>
              <p className="text-[11px] uppercase tracking-eyebrow text-ink-muted mb-3">
                {venture.category}
              </p>
              <h3 className="font-display text-2xl mb-3">{venture.name}</h3>
              <p className="text-sm text-ink-soft leading-relaxed">{venture.description}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </motion.section>
  );
};

export default VenturesSection;
